/**
 * TenantScreening — landlord portal page at /screening
 *
 * Workflow:
 *   1. Landlord enters tenant first+last+email → POST /api/screening/start
 *   2. Tenant gets a consent email with a one-time link
 *   3. Tenant submits SIN/DOB/address with explicit PIPEDA consent
 *   4. Backend runs the bureau pull (or returns a SANDBOX report when
 *      no provider creds are configured) and surfaces the score + risk
 *      band here. Stars are rendered from the actual numeric data —
 *      never fabricated.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Loader2, Plus, RefreshCw, Mail, Clock, CheckCircle2,
  AlertCircle, Trash2, Copy, ExternalLink, Lock, Scale, Settings2,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Button } from "@/components/ui/Primitives";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const STATUS_PILL = {
  awaiting_consent: { label: "Awaiting tenant consent", tone: "amber", icon: Clock },
  completed:        { label: "Completed",                tone: "emerald", icon: CheckCircle2 },
  failed:           { label: "Failed",                   tone: "rose",    icon: AlertCircle },
  cancelled:        { label: "Cancelled",                tone: "slate",   icon: Trash2 },
};

const DECISION_PILL = {
  auto_approve: { label: "Auto-approve", tone: "emerald" },
  review:       { label: "Review",       tone: "amber"   },
  decline:      { label: "Decline",      tone: "rose"    },
};

const RISK_TONE = { low: "emerald", moderate: "amber", elevated: "rose" };

function pillClass(tone) {
  return {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber:   "bg-amber-50 text-amber-700 border-amber-200",
    rose:    "bg-rose-50 text-rose-700 border-rose-200",
    slate:   "bg-slate-50 text-slate-700 border-slate-200",
  }[tone] || "bg-slate-50 text-slate-700 border-slate-200";
}

function StartScreeningForm({ userId, onCreated }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", note: "" });
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e?.preventDefault?.();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      toast.error("First name, last name, and email are required.");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/screening/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": userId },
        body: JSON.stringify({
          first_name: form.first_name.trim(), last_name: form.last_name.trim(),
          email: form.email.trim().toLowerCase(),
          phone: form.phone.trim() || null, note: form.note.trim() || null,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || `HTTP ${r.status}`);
      toast.success(d.email_sent
        ? "Consent email sent to the applicant."
        : "Screening created — copy the consent link to the applicant.");
      setForm({ first_name: "", last_name: "", email: "", phone: "", note: "" });
      onCreated?.(d);
    } catch (err) {
      toast.error(String(err.message || err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="screening-start-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">First name *</label>
          <input data-testid="screening-first" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
            className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500" required/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Last name *</label>
          <input data-testid="screening-last" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
            className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500" required/>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Email *</label>
          <input data-testid="screening-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500" required/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Phone</label>
          <input data-testid="screening-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Note (optional)</label>
        <input data-testid="screening-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
          placeholder="e.g. unit 4B at 123 Main St"
          className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
      </div>
      <Button type="submit" disabled={busy} data-testid="screening-submit">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Sending…</> : <><Plus className="w-4 h-4"/> Send consent link</>}
      </Button>
      <p className="text-[10px] text-slate-500 flex items-start gap-1">
        <Lock className="w-3 h-3 mt-0.5 flex-none"/>
        Compliant pull — the tenant must consent and supply SIN, DOB and address before any bureau is queried. We never store the full SIN in plain text.
      </p>
    </form>
  );
}

function ScoreDial({ score, max = 900 }) {
  if (typeof score !== "number") return null;
  const pct = Math.max(0, Math.min(1, (score - 300) / (max - 300)));
  const angle = 180 * pct;
  const tone = score >= 760 ? "emerald" : score >= 680 ? "amber" : "rose";
  const toneHex = { emerald: "#059669", amber: "#d97706", rose: "#e11d48" }[tone];
  return (
    <div className="relative inline-block" style={{ width: 140 }} data-testid="screening-score-dial">
      <svg viewBox="0 0 100 60" width="140" height="84">
        <path d="M 5 55 A 45 45 0 0 1 95 55" fill="none" stroke="#E5E7EB" strokeWidth="10" strokeLinecap="round"/>
        <path
          d="M 5 55 A 45 45 0 0 1 95 55"
          fill="none"
          stroke={toneHex}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={`${(angle / 180) * 141.37} 141.37`}
        />
      </svg>
      <div className="text-center -mt-3">
        <div className="font-display text-3xl font-extrabold text-slate-900 tabular-nums" data-testid="screening-score-value">{score}</div>
        <div className="text-[10px] uppercase tracking-wide font-bold text-slate-500">credit score</div>
      </div>
    </div>
  );
}

function ScreeningCard({ s, onChange }) {
  const meta = STATUS_PILL[s.status] || STATUS_PILL.awaiting_consent;
  const Icon = meta.icon;
  const link = `${window.location.origin}/screening/consent/${s.id || ""}`; // fallback display only
  const isPending = s.status === "awaiting_consent";
  const isComplete = s.status === "completed" && s.report;

  async function copyLink() {
    const url = s.consent_link || link;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Consent link copied");
    } catch {
      toast.error("Couldn't copy — long-press the URL to copy manually");
    }
  }

  async function cancel() {
    if (!window.confirm("Cancel this screening request?")) return;
    try {
      const r = await fetch(`${API}/api/screening/${s.id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("crc_session_token") || ""}`,
        },
      });
      if (r.ok) {
        toast.success("Cancelled");
        onChange?.();
        return;
      }
      // Surface a real reason instead of the generic "Could not cancel" — the
      // previous silent fail hid auth expiries and 404s from the user.
      let detail = "";
      try { detail = (await r.json())?.detail || ""; } catch { /* ignore */ }
      if (r.status === 401 || r.status === 403) {
        toast.error("Session expired. Log back in and try again.");
      } else if (r.status === 404) {
        toast.error("Screening no longer exists.");
        onChange?.();
      } else {
        toast.error(detail || `Could not cancel (${r.status})`);
      }
    } catch (err) {
      toast.error(err?.message || "Network error — check your connection.");
    }
  }

  const [evalRent, setEvalRent] = useState("");
  const [evalEmp, setEvalEmp] = useState("");
  const [evalBusy, setEvalBusy] = useState(false);
  async function evaluate() {
    setEvalBusy(true);
    try {
      const body = {};
      if (evalRent) body.monthly_rent_cents = Math.round(Number(evalRent) * 100);
      if (evalEmp)  body.months_employment = parseInt(evalEmp, 10);
      const r = await fetch(`${API}/api/screening/${s.id}/evaluate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("crc_session_token") || ""}`,
        },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d?.detail || "Could not evaluate");
        return;
      }
      toast.success(`Decision: ${d.decision.replace("_", " ")}`);
      onChange?.();
    } catch (err) {
      toast.error(err?.message || "Network error");
    } finally {
      setEvalBusy(false);
    }
  }

  return (
    <Card className="p-4" data-testid={`screening-card-${s.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="font-display text-base font-bold text-slate-900 truncate" data-testid={`screening-name-${s.id}`}>
            {s.first_name} {s.last_name}
          </div>
          <div className="text-xs text-slate-500 truncate">{s.email}{s.phone ? ` · ${s.phone}` : ""}</div>
          {s.note && <div className="text-xs text-slate-500 mt-0.5 truncate">{s.note}</div>}
        </div>
        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] uppercase tracking-wide font-bold border ${pillClass(meta.tone)}`} data-testid={`screening-status-${s.id}`}>
          <Icon className="w-3 h-3"/> {meta.label}
        </span>
      </div>

      {isPending && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button onClick={copyLink} data-testid={`screening-copy-link-${s.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-slate-200 hover:bg-slate-50 text-xs font-bold text-slate-700">
            <Copy className="w-3 h-3"/> Copy consent link
          </button>
          {s.consent_link && (
            <a href={s.consent_link} target="_blank" rel="noopener noreferrer" data-testid={`screening-open-link-${s.id}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-slate-200 hover:bg-slate-50 text-xs font-bold text-emerald-700">
              <ExternalLink className="w-3 h-3"/> Open
            </a>
          )}
          <button onClick={cancel} data-testid={`screening-cancel-${s.id}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-rose-200 hover:bg-rose-50 text-xs font-bold text-rose-700">
            <Trash2 className="w-3 h-3"/> Cancel
          </button>
          <span className="text-[10px] text-slate-500 inline-flex items-center gap-1 ml-auto"><Mail className="w-3 h-3"/> Email sent to {s.email}</span>
        </div>
      )}

      {isComplete && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 items-center" data-testid={`screening-report-${s.id}`}>
          <ScoreDial score={s.report.score}/>
          <div className="sm:col-span-2 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide font-bold border ${pillClass(RISK_TONE[s.report.risk_band] || "slate")}`}
                data-testid={`screening-risk-${s.id}`}>
                Risk: {s.report.risk_band || "n/a"}
              </span>
              {s.report.id_verified && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide font-bold border bg-emerald-50 text-emerald-700 border-emerald-200">
                  <ShieldCheck className="w-3 h-3"/> ID verified
                </span>
              )}
              {s.report.sandbox && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide font-bold border bg-amber-50 text-amber-700 border-amber-200"
                  title="Bureau credentials not configured — this is a deterministic test report, not a real credit pull"
                  data-testid={`screening-sandbox-${s.id}`}>
                  Sandbox
                </span>
              )}
            </div>
            <div className="text-xs text-slate-600 grid grid-cols-2 gap-y-1">
              <div>Open collections</div><div className="font-bold text-slate-900 text-right">{s.report.open_collections ?? "—"}</div>
              <div>Previous evictions</div><div className="font-bold text-slate-900 text-right">{s.report.previous_evictions ?? "—"}</div>
              <div>Provider</div><div className="font-bold text-slate-900 text-right">{s.report.provider || "—"}</div>
            </div>
            {s.report.summary && <p className="text-[11px] text-slate-500 leading-relaxed">{s.report.summary}</p>}
          </div>
        </div>
      )}

      {isComplete && (
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid={`screening-decision-${s.id}`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700">
              <Scale className="w-4 h-4" /> Decision engine
            </div>
            {s.decision ? (
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide font-bold border ${pillClass(DECISION_PILL[s.decision.decision]?.tone || "slate")}`}
                data-testid={`screening-decision-pill-${s.id}`}
                title={s.decision.reason}
              >
                {DECISION_PILL[s.decision.decision]?.label || s.decision.decision}
              </span>
            ) : (
              <span className="text-[10px] uppercase tracking-wide text-slate-400">Not evaluated yet</span>
            )}
          </div>
          {s.decision?.reason && (
            <p className="mt-1.5 text-[11px] text-slate-600 leading-relaxed">{s.decision.reason}</p>
          )}
          <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
            <label className="text-[11px] text-slate-600">
              Monthly rent (CAD)
              <input
                type="number" min="0" step="50" value={evalRent}
                onChange={(e) => setEvalRent(e.target.value)}
                placeholder="2250"
                className="mt-1 w-full px-2 py-1.5 rounded-md border border-slate-300 text-xs"
                data-testid={`screening-eval-rent-${s.id}`}
              />
            </label>
            <label className="text-[11px] text-slate-600">
              Months employed
              <input
                type="number" min="0" step="1" value={evalEmp}
                onChange={(e) => setEvalEmp(e.target.value)}
                placeholder="12"
                className="mt-1 w-full px-2 py-1.5 rounded-md border border-slate-300 text-xs"
                data-testid={`screening-eval-emp-${s.id}`}
              />
            </label>
            <button
              onClick={evaluate} disabled={evalBusy}
              data-testid={`screening-evaluate-btn-${s.id}`}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-bold hover:bg-emerald-700 disabled:opacity-50"
            >
              {evalBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Scale className="w-3 h-3" />}
              {s.decision ? "Re-evaluate" : "Evaluate"}
            </button>
          </div>
        </div>
      )}

      {s.status === "failed" && s.report?.detail && (
        <div className="mt-3 rounded-lg bg-rose-50 border border-rose-200 p-3 text-xs text-rose-800">
          {s.report.detail}
        </div>
      )}
    </Card>
  );
}

export default function TenantScreening() {
  const { user } = useAuth();
  const userId = user?.user_id || user?.id || user?._id;
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ provider_configured: false, provider: "sandbox" });
  const [loading, setLoading] = useState(true);
  const [rulesOpen, setRulesOpen] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/screening/list`, { headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token")||""}`, "x-user-id": userId } });
      const d = await r.json();
      if (r.ok) {
        setItems(d.items || []);
        setMeta({ provider_configured: !!d.provider_configured, provider: d.provider || "sandbox" });
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  // Light polling so the landlord sees status updates without a refresh
  // when the tenant submits consent.
  useEffect(() => {
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  function onCreated(payload) {
    // Optimistic — re-fetch to surface the new row in the list
    load();
    if (payload?.consent_link) {
      // Drop the link on the clipboard so the landlord can DM/SMS it too.
      try { navigator.clipboard.writeText(payload.consent_link); } catch { /* silent */ }
    }
  }

  if (!userId) return <div className="p-6 text-sm text-slate-500">Login required.</div>;

  return (
    <div data-testid="tenant-screening-page">
      <PageHeader
        title="Smart Tenant Screening"
        subtitle="Send a consent-based credit + ID check to any applicant. Compliant under PIPEDA — the tenant supplies SIN/DOB/address with explicit consent before the bureau is queried."
        actions={
          <>
            <Button variant="secondary" onClick={() => setRulesOpen(true)} data-testid="screening-rules-btn">
              <Settings2 className="w-4 h-4"/> Rules
            </Button>
            <Button variant="secondary" onClick={load} data-testid="screening-refresh">
              <RefreshCw className="w-4 h-4"/> Refresh
            </Button>
          </>
        }
      />

      {rulesOpen && <DecisionRulesModal onClose={() => setRulesOpen(false)} />}

      {!meta.provider_configured && (user?.role === "admin") && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 mb-4 text-xs text-amber-900 flex items-start gap-2" data-testid="screening-sandbox-banner">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-none"/>
          <div>
            <b>Sandbox mode (admins only).</b> Live bureau credentials aren't configured yet. Reports return deterministic test data clearly flagged as <code>sandbox</code>. To enable live Equifax / TransUnion / Certn pulls, add <code>CERTN_API_KEY</code> + <code>SCREENING_PROVIDER</code> + <code>CERTN_LIVE_ENABLED=true</code> to the backend env.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-1">
          <div className="font-display text-base font-bold text-slate-900 mb-3">New screening</div>
          <StartScreeningForm userId={userId} onCreated={onCreated}/>
        </Card>

        <div className="lg:col-span-2 space-y-3" data-testid="screening-list">
          {loading ? (
            <Card className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-emerald-600 mx-auto"/></Card>
          ) : items.length === 0 ? (
            <Card className="p-10 text-center">
              <ShieldCheck className="w-8 h-8 text-emerald-600 mx-auto mb-2"/>
              <div className="font-display text-base font-bold text-slate-900">No screenings yet</div>
              <p className="text-xs text-slate-500 mt-1">Start one on the left — your applicant gets a one-time consent link by email.</p>
            </Card>
          ) : (
            items.map((s) => <ScreeningCard key={s.id} s={{ ...s, landlord_id: userId }} onChange={load}/>)
          )}
        </div>
      </div>

      <div className="mt-6 text-xs text-slate-500">
        <Link to="/legal/privacy" className="underline">Privacy &amp; data-handling</Link>
        {" · "}
        <Link to="/legal/screening" className="underline">Screening consent disclosure</Link>
      </div>
    </div>
  );
}

function DecisionRulesModal({ onClose }) {
  const [rules, setRules] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/screening/decision-rules`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("crc_session_token") || ""}` },
        });
        const d = await r.json();
        setRules(d.rules);
        setDefaults(d.defaults);
      } catch (e) {
        toast.error("Couldn't load rules");
      }
    })();
  }, []);

  async function save() {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/screening/decision-rules`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("crc_session_token") || ""}`,
        },
        body: JSON.stringify(rules),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        toast.error(d?.detail || "Save failed");
        return;
      }
      toast.success("Rules saved");
      onClose();
    } finally {
      setSaving(false);
    }
  }

  if (!rules) {
    return (
      <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="screening-rules-modal">
        <div className="bg-white rounded-xl p-8"><Loader2 className="w-5 h-5 animate-spin text-emerald-600" /></div>
      </div>
    );
  }

  const upd = (k, v) => setRules((r) => ({ ...r, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="screening-rules-modal" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-4">
          <Scale className="w-5 h-5 text-emerald-700" />
          <h3 className="font-display font-bold text-slate-900">Decision rules</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Thresholds applied when you tap Evaluate on a completed screening. Fully transparent — every tenant decision shows which checks passed or failed.
        </p>
        <div className="space-y-3">
          <RuleRow label="Min credit score" value={rules.min_credit_score} min={300} max={900} step={5} onChange={(v) => upd("min_credit_score", v)} testId="rule-credit" />
          <RuleRow label="Max open collections" value={rules.max_open_collections} min={0} max={20} step={1} onChange={(v) => upd("max_open_collections", v)} testId="rule-collections" />
          <RuleRow label="Max rent-to-income ratio" value={rules.max_rent_to_income_ratio} min={0.1} max={1.0} step={0.01} onChange={(v) => upd("max_rent_to_income_ratio", v)} testId="rule-ratio" />
          <RuleRow label="Min months employed" value={rules.min_months_employment} min={0} max={60} step={1} onChange={(v) => upd("min_months_employment", v)} testId="rule-emp" />
          <label className="flex items-center justify-between text-xs">
            <span className="text-slate-700">Require government ID verified</span>
            <input type="checkbox" checked={rules.require_id_verified} onChange={(e) => upd("require_id_verified", e.target.checked)} data-testid="rule-id" />
          </label>
          <label className="flex items-center justify-between text-xs">
            <span className="text-slate-700">Allow applicants with previous evictions</span>
            <input type="checkbox" checked={rules.allow_previous_evictions} onChange={(e) => upd("allow_previous_evictions", e.target.checked)} data-testid="rule-evictions" />
          </label>
        </div>
        <div className="mt-5 flex items-center justify-between">
          {defaults && (
            <button
              onClick={() => setRules({ ...defaults })}
              className="text-[11px] text-slate-500 underline hover:text-slate-700"
              data-testid="rule-reset"
            >
              Reset to defaults
            </button>
          )}
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 rounded-md text-xs font-bold text-slate-700 bg-slate-100 hover:bg-slate-200">Cancel</button>
            <button onClick={save} disabled={saving} className="px-3 py-1.5 rounded-md text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50" data-testid="rule-save">
              {saving ? "Saving…" : "Save rules"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const RuleRow = ({ label, value, min, max, step, onChange, testId }) => (
  <label className="block text-xs">
    <div className="flex items-center justify-between mb-1">
      <span className="text-slate-700">{label}</span>
      <span className="font-mono text-slate-900 tabular-nums">{value}</span>
    </div>
    <input
      type="range" min={min} max={max} step={step} value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full"
      data-testid={testId}
    />
  </label>
);

